import type { Shape } from './fleet-reports-pl';
import type { fleetReportsPlOps } from './fleet-reports-pl-ops';

/** Druga część słownika EN (zgłoszenia, checklisty, szablony, serwisy, zakładki). Typowana względem PL — brak klucza = błąd kompilacji. */
export const fleetReportsEnOps: Shape<typeof fleetReportsPlOps> = {
  incidents: {
    title: 'Incidents',
    description:
      'Breakdowns, damage, collisions and irregularities reported by drivers — handling and vehicle decisions.',
    search: 'Search by title, plate, reporter…',
    chipOpen: 'Open',
    chipDecision: 'Awaiting decision',
    empty: 'No incidents to show.',
    emptyFiltered: 'No incident matches the filters.',
    filterSeverity: 'Severity',
    filterType: 'Type',
    allSeverities: 'Any severity',
    allTypes: 'All types',
    onlyOpen: 'Open only',
    onlyDecision: 'Awaiting decision',
    onlyIncomplete: 'Incomplete',
    incomplete: 'Missing attachments: {count}',
    incompleteHint:
      'The template requires more attachments than were added. The incident is not blocked — photos can be added later.',
    blocksVehicle: 'Blocks the vehicle',
    col: {
      date: 'Reported',
      vehicle: 'Vehicle',
      title: 'Title',
      type: 'Type',
      severity: 'Severity',
      reporter: 'Reporter',
      decision: 'Decision',
      status: 'Status',
    },
    detail: {
      summary: 'Incident details',
      reporter: 'Reporter',
      occurredAt: 'Occurred',
      location: 'Location',
      odometer: 'Odometer',
      description: 'Description',
      fields: 'Form data',
      noFields: 'The template has no extra fields.',
      resolution: 'Resolution',
      assigned: 'Assigned to',
      unassigned: 'Unassigned',
      assignedSaved: 'Assignment saved.',
      fromChecklist: 'Incident from a checklist',
      openChecklist: 'Open checklist',
      photosTitle: 'Photos and documents',
      statusTitle: 'Change status',
      statusHint: 'Only transitions allowed from the current status are available.',
      statusResolution: 'Resolution description (optional)',
      statusComment: 'Comment on the change (optional)',
      statusNone: 'The incident is closed — no further transitions.',
      statusChanged: 'Incident status changed.',
      commentTitle: 'Handler comment',
      commentPlaceholder: 'Add a comment — the reporter gets a notification…',
      commentAdd: 'Add comment',
      commentAdded: 'Comment added.',
      transition: '{from} → {to}',
      blocked: 'The vehicle was blocked (status “In service”).',
      automatic: 'automatically',
      valueYes: 'yes',
      valueNo: 'no',
    },
    decision: {
      bannerTitle: 'Vehicle {plate} is awaiting a decision',
      bannerBody: 'The reported defect is serious. Until you decide, the vehicle cannot start a new logbook.',
      bannerVehicle:
        'The vehicle is awaiting a decision after a reported defect — a new logbook cannot be started.',
      open: 'Make a decision',
      openIncident: 'View incident',
      title: 'Vehicle decision',
      description: 'Decide whether vehicle {plate} may drive despite the reported defect.',
      allow: 'Clear to drive',
      block: 'Block vehicle',
      allowHint: 'The vehicle stays active and can start a logbook.',
      blockHint: 'The vehicle goes to “In service” until repaired.',
      comment: 'Reason for the decision',
      commentRequired: 'Provide a reason (min. 3 characters).',
      allowed: 'Vehicle cleared to drive.',
      blocked: 'Vehicle blocked.',
      decidedBy: 'Decided by: {name}',
    },
  },
  checklists: {
    title: 'Checklists',
    description: 'Completed driver checklists before and after trips — answers, photos and reported defects.',
    chipTotal: 'Checklists',
    chipDefects: 'With defects',
    empty: 'No completed checklists.',
    emptyFiltered: 'No checklist matches the filters.',
    filterPhase: 'Phase',
    allPhases: 'Both phases',
    onlyDefects: 'Only with defects',
    col: {
      date: 'Submitted',
      vehicle: 'Vehicle',
      driver: 'Driver',
      template: 'Checklist',
      phase: 'Phase',
      result: 'Result',
    },
    resultOk: 'No issues',
    resultWarning: 'Warnings',
    resultDefect: 'Defect',
    resultCritical: 'Critical defect',
    detail: {
      summary: 'Summary',
      template: 'Template',
      version: 'version {n}',
      submittedAt: 'Submitted',
      comment: 'Driver comment',
      incident: 'Incident created',
      openIncident: 'Open incident',
      answers: 'Answers',
      critical: 'Critical item',
      noResult: 'no answer',
      noComment: 'No comment',
      photos: 'Photos: {count}',
      noPhotos: 'No photos',
      photosOf: 'Photos for the item',
    },
    requirements: {
      title: 'Forms required before closing the logbook',
      ok: 'All required forms are completed.',
      missing: 'Forms are missing — the driver cannot close this logbook.',
    },
  },
  templates: {
    title: 'Templates',
    description: 'Checklist and incident templates used by drivers in the app.',
    tabChecklists: 'Checklists',
    tabIncidents: 'Incidents',
    showInactive: 'Show inactive versions',
    add: 'New template',
    empty: 'No templates.',
    inactive: 'Inactive',
    active: 'Active',
    allVehicleTypes: 'all vehicle types',
    itemsCount: 'Questions: {count}',
    fieldsCount: 'Fields: {count}',
    versionN: 'version {n}',
    versions: 'Versions',
    versionsTitle: 'Versions of “{name}”',
    versionsHint:
      'Every change to the questions creates a new version. Completed checklists stay with the version they were filled in.',
    versionCurrent: 'current',
    view: 'View',
    deactivate: 'Disable',
    activate: 'Enable',
    toggled: 'Template availability changed.',
    checklist: {
      createTitle: 'New checklist template',
      editTitle: 'Edit checklist template',
      name: 'Name',
      code: 'Code (stable across versions)',
      codeHint: 'Empty = derived from the name. It cannot be changed after saving.',
      description: 'Description',
      phase: 'Phase',
      vehicleTypes: 'Vehicle types',
      vehicleTypesHint: 'None selected = all types.',
      items: 'Questions',
      addItem: 'Add question',
      question: 'Question',
      helpText: 'Hint for the driver',
      photo: 'Photo',
      commentReq: 'Comment',
      critical: 'Critical item',
      criticalHint:
        'A defect on this item is a serious defect — it triggers notifications and the procedure from fleet settings.',
      moveUp: 'Move question up',
      moveDown: 'Move question down',
      removeItem: 'Remove question',
      versioningNote:
        'Changing questions or requirements creates a new version {next}; existing submissions stay with version {current}.',
      versioningNone: 'Changing only the name, description or vehicle types is saved in the same version.',
      savedNew: 'Checklist template created.',
      savedVersion: 'New template version saved.',
      savedInPlace: 'Template changes saved.',
      resultsNote: 'An answer is always one of: OK / warning / defect.',
      inactiveReadonly: 'This is an inactive version and cannot be edited. Edit the current version.',
    },
    incident: {
      createTitle: 'New incident template',
      editTitle: 'Edit incident template',
      name: 'Name',
      description: 'Description',
      type: 'Incident type',
      severity: 'Default severity',
      minAttachments: 'Minimum number of attachments',
      minAttachmentsHint:
        'An incident without the required photos is still saved, but marked incomplete (0–20).',
      fields: 'Form fields',
      addField: 'Add field',
      fieldLabel: 'Label',
      fieldKey: 'Key',
      fieldKeyHint: 'Letters without diacritics and digits, starting with a lowercase letter.',
      fieldType: 'Type',
      fieldRequired: 'Required',
      fieldOptions: 'Options (one per line)',
      removeField: 'Remove field',
      saved: 'Incident template saved.',
    },
    issues: {
      NAME_SHORT: 'The name must have at least 2 characters.',
      ITEMS_EMPTY: 'Add at least one question.',
      ITEMS_TOO_MANY: 'A template can have at most 60 questions.',
      QUESTION_SHORT: 'Question {n} is too short.',
      FIELD_KEY_INVALID: 'Field {n}: invalid key.',
      FIELD_KEY_DUPLICATE: 'Field {n}: duplicate key.',
      FIELD_LABEL_REQUIRED: 'Field {n}: provide a label.',
      FIELD_OPTIONS_REQUIRED: 'Field {n}: a choice list needs options.',
      MIN_ATTACHMENTS_RANGE: 'Minimum attachments: 0–20.',
    },
  },
  service: {
    title: 'Service and repairs',
    description: 'History of inspections, services, repairs and tyres with costs and next due dates.',
    add: 'Add entry',
    search: 'Search by description, vendor, plate…',
    chipTotal: 'Entries',
    empty: 'No service entries.',
    emptyFiltered: 'No entry matches the filters.',
    filterType: 'Type',
    allTypes: 'All types',
    col: {
      date: 'Date',
      vehicle: 'Vehicle',
      type: 'Type',
      description: 'Description',
      vendor: 'Vendor',
      odometer: 'Odometer',
      cost: 'Cost',
      next: 'Next due',
      status: 'Status',
    },
    nextDue: 'Next service',
    nextDueNone: 'No due date scheduled',
    form: {
      createTitle: 'New service entry',
      editTitle: 'Edit service entry',
      vehicle: 'Vehicle',
      selectVehicle: 'Select a vehicle',
      type: 'Type',
      status: 'Status',
      performedAt: 'Date performed (planned date for planned entries)',
      odometer: 'Odometer (km)',
      odometerHint: 'Completed entries create an odometer reading — it must be consistent with the history.',
      vendor: 'Workshop / vendor',
      description: 'Description of the work',
      cost: 'Cost (PLN)',
      invoice: 'Invoice number',
      nextDueAt: 'Next due (date)',
      nextDueKm: 'Next due (km)',
      incident: 'Linked incident',
      errVehicle: 'Select a vehicle.',
      errDate: 'Enter a date.',
      errDescription: 'The description must have at least 3 characters.',
      errOdometer: 'Odometer: a whole number 0–5,000,000.',
      errCost: 'Cost: a number from 0 to 10,000,000.',
      errNextKm: 'Next mileage: a whole number 0–5,000,000.',
      saved: 'Service entry saved.',
      deleted: 'Service entry deleted.',
      deleteConfirm: 'Delete this service entry? This cannot be undone.',
      documents: 'Documents (invoices, reports)',
      saveFirst: 'Documents can be added after the entry is saved.',
    },
  },
  vtabs: {
    fuel: 'Fuel',
    expenses: 'Expenses',
    incidents: 'Incidents',
    checklists: 'Checklists',
    service: 'Service',
    recent: 'Recent entries',
    count: 'Total: {count}',
    openCount: 'Open: {count}',
    seeAll: 'All entries',
    emptyFuel: 'This vehicle has no fuel entries yet.',
    emptyExpenses: 'This vehicle has no expenses yet.',
    emptyIncidents: 'No incidents for this vehicle.',
    emptyChecklists: 'No completed checklists for this vehicle.',
    emptyService: 'No service entries for this vehicle.',
    addService: 'Add service entry',
    nextDueAt: 'by {date}',
    nextDueKm: 'at {km}',
  },
  vehicleExtra: {
    tankCapacity: 'Tank capacity (l)',
    tankCapacityHint: 'Optional — helps catch fills larger than the tank.',
    tankCapacityError: 'Tank capacity: a number from 1 to 2000.',
    tankCapacityField: 'Tank capacity',
  },
  settingsExtra: {
    formsTitle: 'Forms required when closing a logbook',
    formsDescription:
      'The requirement applies only when an active checklist template of that phase exists for the vehicle type.',
    checklistBefore: 'Require a pre-trip checklist',
    checklistAfter: 'Require a post-trip checklist',
    fuelTitle: 'Fuel entries',
    fuelDescription: 'A receipt helps verify amounts and litres.',
    fuelReceipt: 'Require a receipt photo to approve a fuel entry',
    defectTitle: 'Procedure after a serious defect',
    defectDescription:
      'A serious defect is a critical incident, one that blocks the vehicle, or a critical checklist item. A notification is always sent to people handling incidents.',
    defectLabel: 'What happens to the vehicle',
    templatesHint: 'Edit checklist questions and incident fields in the “Templates” tab.',
    openTemplates: 'Go to templates',
  },
};
